import { Typography } from '~/components/common/Typography'

export interface FlowsLoadingSkeletonProps {
  repository?: string
  branch?: string
  isS3Upload?: boolean
  displayRepoName?: string
  className?: string
}

const SKELETON_NAME_WIDTHS = ['60%', '45%', '75%', '50%', '40%', '65%']

export function FlowsLoadingSkeleton({
  repository = 'curie-dev/prc-curie-employee-sync',
  branch = 'main',
  isS3Upload = false,
  displayRepoName,
  className
}: FlowsLoadingSkeletonProps) {
  return (
    <div
      className={`flex h-full w-full flex-col items-center overflow-y-auto overflow-x-hidden px-[24px] pb-[436px] pt-[70px] ${className || ''}`}
      role='status'
      aria-busy='true'
      aria-label='Loading flows'
    >
      <div className='flex w-full max-w-[520px] flex-col gap-[48px]'>
        {/* Title Section */}
        <div className='flex w-full flex-col items-center gap-[60px]'>
          <div className='flex w-full flex-col items-center gap-[16px]'>
            <Typography
              fontFamily='geist'
              fontSize='titleLg'
              fontWeight='bold'
              className='text-center text-black'
              style={{ fontSize: '20px', fontWeight: 700, lineHeight: '22px' }}
            >
              Choose & configure flows
            </Typography>
            <Typography
              fontFamily='geistMono'
              fontSize='bodySm'
              fontWeight='semibold'
              className='text-center text-[rgba(0,0,0,0.65)]'
              style={{ fontSize: '12px', fontWeight: 600 }}
            >
              {isS3Upload
                ? `Uploaded folder: ${displayRepoName || 'Uploaded Project'}`
                : `Repo & Branch: ${repository}/${branch}`}
            </Typography>
          </div>
        </div>

        {/* Content Area */}
        <div className='flex w-full flex-col gap-[16px]'>
          <div className='flex w-full flex-col gap-[24px]'>
            {/* Auto-generate Card Skeleton */}
            <div className='flex items-center justify-between gap-[16px] rounded-[16px] border border-[rgba(0,0,0,0.02)] bg-[rgba(189,189,189,0.08)] px-[24px] py-[16px]'>
              <div className='flex flex-1 flex-col gap-[6px]'>
                <div className='h-[14px] w-[70%] animate-pulse rounded-[4px] bg-[rgba(0,0,0,0.07)]' />
                <div className='h-[12px] w-[55%] animate-pulse rounded-[4px] bg-[rgba(0,0,0,0.05)]' />
              </div>
              <div className='h-[24px] w-[44px] shrink-0 animate-pulse rounded-full bg-[rgba(0,0,0,0.07)]' />
            </div>

            {/* Extracted Flows Section Skeleton */}
            <div className='flex w-full flex-col gap-[16px] rounded-[28px] bg-[rgba(189,189,189,0.08)] px-[24px] py-[16px]'>
              {/* Section Header Skeleton */}
              <div className='flex items-center justify-between px-[24px] py-[8px]'>
                <div className='h-[14px] w-[100px] animate-pulse rounded-[4px] bg-[rgba(0,0,0,0.08)]' />
                <div className='h-[28px] w-[80px] animate-pulse rounded-[8px] bg-[rgba(0,0,0,0.05)]' />
              </div>

              {/* Flow Item Skeletons */}
              <div className='flex flex-col gap-[8px]'>
                {SKELETON_NAME_WIDTHS.map((width, index) => (
                  <div
                    key={index}
                    className='flex h-[48px] w-full items-center rounded-[16px] bg-[rgba(0,0,0,0.02)] py-[4px] pl-[12px] pr-[12px]'
                  >
                    <div className='flex items-center gap-[8px] min-w-0'>
                      {/* Checkbox skeleton */}
                      <div className='flex size-[36px] shrink-0 items-center justify-center rounded-[12px]'>
                        <div className='size-[16px] animate-pulse rounded-full bg-[rgba(0,0,0,0.07)]' />
                      </div>
                      {/* Flow name skeleton */}
                      <div
                        className='h-[14px] animate-pulse rounded-[4px] bg-[rgba(0,0,0,0.07)]'
                        style={{ width }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FlowsLoadingSkeleton
